/**
 * @fileoverview NlEntryComposer — the plain-English time entry box
 *
 * Purpose: One implementation of "describe your time and let Morgan code it",
 * shared by the timesheets page (where a parse pre-fills the entry row or
 * opens the bulk review card) and the global quick entry dialog (where it
 * pre-fills the Log time form).
 *
 * The component owns the text, the parse call and the parse-failure toast.
 * What happens with the results is the caller's decision, so a single entry
 * and a batch both arrive through `onParsed`.
 *
 * Dictation (MOR-1456) fills the same box: a director says "on Tuesday on
 * Acme I reviewed the ROCAP, two units, and one unit on the Beta circular",
 * checks the transcript, and presses Add. The parser does the splitting.
 *
 * Dependencies:
 * - Convex: timesheets.draftAi.parseEntry
 */

import { useEffect, useRef, useState } from "react";
import { Loader2, MessagesSquare, Sparkles } from "lucide-react";
import { useAction } from "convex/react";
import { toast } from "sonner";

import { api } from "@cvx/_generated/api";
import type { Id } from "@cvx/_generated/dataModel";

import { extractErrorMessage } from "@/utils/errors";
import { Button } from "@/ui/primitives";
import { Textarea } from "@/ui/primitives/textarea";
import { DictationButton } from "@/ui/composed/inputs/dictation-button";
import { useDictatedField } from "@/ui/hooks/use-dictated-field";

/** One entry proposal returned by the parser, ready for review. */
export interface ParsedEntry {
  jobId?: Id<"jobs">;
  jobName?: string;
  categoryId?: Id<"timeCategories">;
  categoryName?: string;
  date: string;
  narrative: string;
  units?: number;
  billable: boolean;
  projectCode: "pre" | "post";
  unresolved: string[];
}

export function NlEntryComposer({
  orgId,
  baseDate,
  onParsed,
  onExpandChat,
  hint,
  submitLabel = "Add entries",
  autoFocus,
  className,
}: {
  orgId: Id<"organizations">;
  /** Date relative days ("yesterday") resolve against. */
  baseDate: string;
  onParsed: (entries: ParsedEntry[]) => void;
  /** Omitted where there is no chat surface to hand off to. */
  onExpandChat?: (text: string) => void;
  hint: string;
  submitLabel?: string;
  autoFocus?: boolean;
  className?: string;
}) {
  const parseEntry = useAction(api.timesheets.draftAi.parseEntry);
  const [text, setText] = useState("");
  const [parsing, setParsing] = useState(false);
  const dictation = useDictatedField({ value: text, onChange: setText });

  // One line tall at rest; grows a row per Shift+Enter line.
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight}px`;
  }, [dictation.displayValue]);

  const handleParse = async () => {
    if (!text.trim() || parsing || dictation.voice.isActive) return;
    setParsing(true);
    try {
      // Each line is its own entry, guaranteed: one parse call per line, so
      // the model can never merge two lines into one. A single line can
      // still yield several entries ("2 units X and 3 units Y").
      const lines = text
        .split(/\n+/)
        .map((line) => line.trim())
        .filter(Boolean);
      const results = (await Promise.all(
        lines.map((line) => parseEntry({ orgId, text: line, baseDate })),
      )) as { entries: ParsedEntry[] }[];
      const parsedEntries = results.flatMap((r) => r.entries);
      setText("");
      if (parsedEntries.length > 0) onParsed(parsedEntries);
    } catch (err) {
      toast.error(extractErrorMessage(err, "Could not parse that entry."));
    } finally {
      setParsing(false);
    }
  };

  return (
    <div
      className={
        className ??
        "overflow-hidden rounded-xl bg-background shadow-[0_1px_3px_rgba(0,0,0,0.08),0_1px_2px_rgba(0,0,0,0.06)] ring-1 ring-primary/15"
      }
    >
      <div className="relative">
        <Sparkles
          className="pointer-events-none absolute left-3 top-3.5 size-4 text-primary"
          aria-hidden
        />
        <Textarea
          value={dictation.displayValue}
          readOnly={dictation.readOnly}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              void handleParse();
            }
          }}
          ref={textareaRef}
          autoFocus={autoFocus}
          placeholder={
            dictation.voice.isListening
              ? "Listening — say what you did, on which matter, for how long…"
              : "Log draft timesheet by typing or dictating — e.g. 3 units reviewing voidable transactions on Apex Construction"
          }
          rows={1}
          className="max-h-40 min-h-0 w-full resize-none overflow-y-auto rounded-none border-0 bg-transparent py-3 pl-10 pr-4 text-sm shadow-none focus-visible:ring-0"
          aria-label="AI draft"
        />
      </div>
      <div className="flex flex-wrap items-center justify-between gap-2 border-t border-border/50 px-3 py-2">
        <div className="flex min-w-0 items-center gap-2 pl-1">
          <span className="shrink-0 rounded-md bg-primary/10 px-1.5 py-0.5 text-[11px] font-medium text-primary">
            AI draft
          </span>
          <p className="truncate text-xs text-muted-foreground">{hint}</p>
        </div>
        <div className="flex items-center gap-2">
          <DictationButton voice={dictation.voice} className="h-8 w-8" />
          {onExpandChat && (
            <Button
              variant="ghost"
              size="sm"
              className="h-8 text-muted-foreground"
              onClick={() => onExpandChat(text.trim())}
              title="Continue in a chat with the timesheet assistant"
            >
              <MessagesSquare className="size-4" />
              Chat
            </Button>
          )}
          <Button
            size="sm"
            className="h-8"
            disabled={!text.trim() || parsing || dictation.voice.isActive}
            onClick={() => void handleParse()}
          >
            {parsing ? (
              <Loader2 className="size-4 animate-spin" />
            ) : (
              <Sparkles className="size-4" />
            )}
            {submitLabel}
          </Button>
        </div>
      </div>
    </div>
  );
}
